import React, { useEffect, useState } from 'react';
import Icons from '../../../../assets/index';
import Button from '../../../../components/Button/Button';
import DeleteSection from '../DeleteSection/DeleteSection';
import Popup from '../../../../components/Popup/Popup';
import Panel from '../../../../components/Panel/Panel';
import AddSection from '../AddSection/AddSection';
import DeleteField from '../DeleteField/DeleteField';
import { useGetSectionsQuery } from '../../../../services/plaintiffSheets/plaintiffSheetsEndpoint';
import { ISectionItem } from '../../../../services/plaintiffSheets/plaintiffSheetsTypes';
import { useParams } from 'react-router-dom';
import AddField from '../Field/AddField/AddField';
import { convertToTitleCase } from '@/utils/stringHandlers';
import AddTab from '../Field/AddField/AddTabs';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DeleteTabsField from '../DeleteField/DeleteTabsField';
import CustomTableConditionWithMultipleFields from '../CustomTableConditionWithMultipleFields/CustomTableConditionWithMultipleFields';
import { useUpdateFieldMutation } from '@/services/fieldApi/FieldApi';
import { useToast } from '@/components/Toast/ToastContext';
import EditOption from '../Field/EditOption';
import EditOptionField from '../Field/EditOptionField';

export interface SectionProps {
  selectedSectionId: string | null;
}

const Section: React.FC<SectionProps> = ({ selectedSectionId }) => {
  const { id } = useParams();

  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFieldOpen, setIsFieldOpen] = useState(false);
  const [isDeleteFieldPopUpOpen, setIsDeleteFieldPopupOpen] = useState(false);
  const [isDeleteTabsFieldPopUpOpen, setIsDeleteTabsFieldPopupOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<ISectionItem | null>(null);
  const [sectionId, setSectionId] = useState<string>('');
  const [fieldData, setFieldData] = useState<any[]>([]);
  const [isTabsOpen, setIsTabsOpen] = useState(false);
  const [expandedTabIndex, setExpandedTabIndex] = useState<number | null>(null);
  const [tabsId, setTabId] = useState<string>('');
  const [FieldTabsId, setFieldTabsId] = useState<string>('');
  const [editOptionModalOpen, setEditOptionModalOpen] = useState(false);
  const [editFieldModalOpen, setEditFieldModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [selectedField, setSelectedField] = useState<any>(null);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState<number | null>(null);
  const [selectedFieldIdx, setSelectedFieldIdx] = useState<number | null>(null);

  const {
    data: sectionData,
    isLoading,
    isError,
    isSuccess,
  } = useGetSectionsQuery(
    {
      factSheetId: id || '',
    },
    {
      skip: !id,
    }
  );

  useEffect(() => {
    if (!isLoading && !isError && isSuccess && sectionData) {
      const responseSections = sectionData.data.pagedSections;

      const result = responseSections.map((section: any) => {
        const responseFields = section.fields.map((field: any) => ({ ...field }));

        const tableFields = section.data_tables.map((table: any) => ({
          field_id: table.table_id,
          field_scope: 'main',
          order: table.order,
          label: table.label,
          field_type: 'table',
          tabName: table.tabName,
          ispopulated: table.ispopulated,
          is_required: false,
          columns: table.columnHeaders,
          rows: table.rowHeaders,
          columnGroups: table.columnGroups,
          additional_config: table.additional_config,
          options: [],
        }));

        const tabGroupFields =
          section.tab_fields?.map((tabField: any) => {
            const transformedTabs = tabField.tabs.map((tab: any) => {
              const tableFieldsInTab = tab.data_tables.map((table: any) => ({
                field_id: table.table_id,
                field_scope: 'main',
                order: table.order,
                label: table.label,
                field_type: 'table',
                is_required: false,
                columns: table.columnHeaders,
                rows: table.rowHeaders,
                additional_config: table.additional_config,
                options: [],
              }));

              return {
                ...tab,
                fields: [...(tab.fields || []), ...tableFieldsInTab],
              };
            });

            return {
              field_id: tabField.tab_field_id,
              field_scope: 'main',
              field_type: 'tabs',
              order: tabField.order,
              label: tabField.label,
              tabs_count: tabField.tabs_count,
              tabs: transformedTabs,
              options: [],
              additional_config: {},
              is_required: false,
            };
          }) || [];

        const allFields = [...responseFields, ...tableFields, ...tabGroupFields];

        const updatedFields = allFields.map((field) => {
          if (
            field.field_scope === 'main' &&
            field.field_type === 'condition_with_multiple_fields'
          ) {
            const updatedOptions = field.options.map((option: any) => {
              if (option.field_id) {
                const conditionField = allFields.find((f: any) => f.field_id === option.field_id);
                return {
                  ...option,
                  field: conditionField,
                };
              }
              return option;
            });
            return {
              ...field,
              options: updatedOptions,
            };
          }
          return field;
        });

        const filteredMainFields = updatedFields
          .filter((f) => f.field_scope === 'main')
          .sort((a, b) => a.order - b.order);

        return {
          ...section,
          fields: filteredMainFields,
          all_fields: updatedFields,
        };
      });

      setFieldData(result);
    }
  }, [isLoading, isError, isSuccess, sectionData]);

  const [UpdateField, { isLoading: fieldUpdateLoading }] = useUpdateFieldMutation();
  const { addToast } = useToast();

  const handleUpdateField = async (sectionId: string, updatedField: any) => {
    const sanitizedField = {
      ...updatedField,
      fieldType: updatedField.field_type || updatedField.fieldType,
      options: updatedField.options?.map((option: any) => ({
        ...option,
        fields: option.fields?.map((field: any) => ({
          ...field,
          fieldType: field.field_type || field.fieldType,
        })),
      })),
    };
    try {
      await UpdateField({
        factSheetId: id,
        sectionId: sectionId,
        fieldId: updatedField.field_id,
        data: sanitizedField,
      }).unwrap();
      addToast('Success', 'Field updated successfully', 'success');
    } catch (error) {
      addToast('Error', 'Failed to update field', 'error');
    }
  };
  const handleAddFieldToTab = (tabIndex: number) => {
    setIsTabsOpen(true);
    setExpandedTabIndex(tabIndex); // Expand the tab where we're adding the field
  };

  const handleTabAdded = (tabIndex: number) => {
    setExpandedTabIndex(tabIndex); // Expand the tab where the field was added
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg ">
      <div className="p-4 mt-4 overflow-auto mb-4 mr-1">
        {sectionData?.data?.pagedSections?.length === 0 ? (
          <div className="flex flex-col items-center justify-center m-auto mt-[10%]">
            <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded-lg mb-4">
              <Icons.documentIcon />
            </div>
            <h2 className="text-lg font-medium mb-2 text-[#4F4F4F]">
              Let’s start building your Fact Sheet
            </h2>
            <p className="text-[#4F4F4F] text-center mb-6">
              Start by adding a section to structure your form and organize the fields effectively.
            </p>
          </div>
        ) : (
          fieldData
            ?.filter((section: ISectionItem) => section.section_id === selectedSectionId)
            .map((section: ISectionItem, index: number) => (
              <div key={index} className="p-4 bg-ds-cool-gray-1000 rounded-xl shadow-lg mb-4">
                <div className="flex justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-ds-gray-800 break-words whitespace-pre-wrap break-all">
                      Section: {section.title}
                    </h2>
                    <p className="text-xs text-ds-gray-400 break-words whitespace-pre-wrap break-all">
                      {section.description}
                    </p>
                  </div>
                  <div className="flex justify-center items-start  gap-4">
                    <div className="flex justify-center items-center gap-4">
                      <button
                        className="mt-[12px]"
                        onClick={() => {
                          setSelectedSection(section);
                          setIsPanelOpen(true);
                        }}
                      >
                        <Icons.editIcon />
                      </button>
                      <button
                        className="mt-3"
                        onClick={() => {
                          setSelectedSection(section);
                          setIsPopUpOpen(true);
                        }}
                      >
                        <Icons.trashIcon />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        color="outlined"
                        size="md"
                        startIcon={<Icons.plusIcon />}
                        type="submit"
                      >
                        Add Group
                      </Button>
                      <Button
                        startIcon={<Icons.plusIcon color="#FFFFFF" />}
                        color="primary"
                        onClick={() => {
                          setIsFieldOpen(true);
                          setSelectedSection(section);
                          setSectionId(section?.section_id);
                        }}
                      >
                        Add Field
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="border-b border-ds-gray-100 w-[80%] mt-4"></div>

                {section.fields.map((field: any) => (
                  <>
                    <div
                      key={field.id}
                      className="bg-white py-4 px-3 rounded-lg shadow-lg flex flex-col mt-4"
                    >
                      <div
                        className={`flex ${field.field_type === 'condition_with_multiple_fields' ? 'flex-col' : 'justify-between'}`}
                      >
                        {field.field_type !== 'table' &&
                          field.field_type !== 'condition_with_multiple_fields' &&
                          field.field_type !== 'tabs' && (
                            <div>
                              <p className="font-semibold">
                                Field Name:{' '}
                                <span className="text-gray-800 break-words whitespace-pre-wrap break-all">
                                  {field.label}
                                </span>
                              </p>
                              <p className="text-sm text-ds-gray-400">
                                Field Type: {convertToTitleCase(field.field_type)}
                              </p>
                            </div>
                          )}
                        {field.field_type === 'condition_with_multiple_fields' && (
                          <CustomTableConditionWithMultipleFields
                            data={field}
                            fieldUpdateLoading={fieldUpdateLoading}
                            sectionId={section.section_id}
                            onSubmitUpdate={handleUpdateField}
                            setIsFieldOpen={setIsFieldOpen}
                            setSelectedSection={setSelectedSection}
                            onEditOption={(option, optionIdx) => {
                              setSelectedOption(option);
                              setSelectedOptionIdx(optionIdx);
                              setEditOptionModalOpen(true);
                            }}
                            onEditField={(field, optionIdx, fieldIdx) => {
                              setSelectedField(field);
                              setSelectedOptionIdx(optionIdx);
                              setSelectedFieldIdx(fieldIdx);
                              setEditFieldModalOpen(true);
                            }}
                          />
                        )}

                        {field.field_type === 'table' && (
                          <div className="flex flex-col">
                            <div>
                              <p className="font-semibold">
                                Field Name:{' '}
                                <span className="text-gray-800 break-words whitespace-pre-wrap break-all">
                                  {field.label}
                                </span>
                              </p>
                            </div>
                            <div className="flex justify-between gap-10 mt-1">
                              <div>
                                <p className="text-sm text-ds-gray-400">
                                  Field Type:{' '}
                                  <span className="text-sm text-ds-gray-400">Table</span>
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-ds-gray-400">
                                  Columns:{' '}
                                  <span className="text-sm text-ds-gray-400">
                                    {field?.additional_config?.dimensions?.columns}
                                  </span>
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-ds-gray-400">
                                  Rows:{' '}
                                  <span className="text-sm text-ds-gray-400">
                                    {field?.additional_config?.dimensions?.rows}
                                  </span>
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-ds-gray-400">
                                  Row Header:{' '}
                                  <span className="text-sm text-ds-gray-400">
                                    {field?.additional_config?.headers?.rowHeader
                                      ? 'Enabled'
                                      : 'Disabled'}
                                  </span>
                                </p>
                              </div>
                              <div>
                                {field?.additional_config?.columnGroupingEnabled && (
                                  <p className="text-sm text-ds-gray-400">
                                    Group Type:{' '}
                                    <span className="text-sm text-ds-gray-400">
                                      {field?.additional_config?.columnGroupingEnabled
                                        ? ' Grouped Table'
                                        : 'Disabled'}
                                    </span>
                                  </p>
                                )}
                              </div>
                              <div>
                                <p className="text-sm text-ds-gray-400">
                                  Additional Rows:{' '}
                                  <span className="text-sm text-ds-gray-400">
                                    {field?.additional_config?.additionalRows
                                      ? 'Enabled'
                                      : 'Disabled'}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {field.field_type !== 'table' &&
                          field.field_type !== 'condition_with_multiple_fields' &&
                          field.field_type !== 'tabs' && (
                            <div className="flex justify-center  gap-4">
                              <button
                                onClick={() => {
                                  setIsFieldOpen(true);
                                  setSelectedSection(field);
                                  setSectionId(section?.section_id);
                                }}
                              >
                                <Icons.editIcon />
                              </button>
                              <button
                                onClick={() => {
                                  setIsDeleteFieldPopupOpen(true);
                                  setSelectedSection(field);
                                  setSectionId(section?.section_id);
                                }}
                              >
                                <Icons.trashIcon />
                              </button>
                            </div>
                          )}
                      </div>
                      {field.field_type !== 'tabs' && (
                        <div className="border-b border-ds-gray-100 w-[95%] mt-2"></div>
                      )}
                      {field.field_type === 'information' ||
                        (field.field_type === 'table' && (
                          <div className="flex items-center gap-2  bg-ds-mc-blue-100 p-2 rounded-md w-[96%] mt-2 ">
                            <Icons.infoIcon />
                            <p className="text-xs  text-ds-orange-900">
                              Field Name works as the title of the modal and does not appear for
                              information, table and tabs.
                            </p>
                          </div>
                        ))}
                      {field.field_type === 'tabs' && (
                        <div className="space-y-4">
                          {field.tabs?.map((tabItem: any, tabIndex: number) => {
                            const isExpanded = expandedTabIndex === tabIndex;

                            return (
                              <div
                                key={tabItem.tab_id}
                                className="rounded-md bg-ds-purple-100 shadow-sm"
                              >
                                {/* Tab Header */}
                                <div className="flex items-center justify-between bg-ds-purple-200 rounded-t-md px-4 py-3">
                                  <span className="text-sm font-semibold text-gray-800">
                                    Tab {tabIndex + 1}: {tabItem.name}
                                  </span>
                                  <div className="flex items-center gap-4">
                                    <button
                                      type="button"
                                      className="text-gray-600 hover:text-red-600"
                                      onClick={() => {
                                        setIsDeleteFieldPopupOpen(true);
                                        setSelectedSection(field);
                                        setSectionId(section?.section_id);
                                      }}
                                    >
                                      <Icons.trashIcon />
                                    </button>
                                    <Button
                                      startIcon={<Icons.plusIcon />}
                                      type="button"
                                      onClick={() => {
                                        handleAddFieldToTab(tabIndex);
                                        setSelectedSection(tabItem);
                                        setSectionId(section?.section_id);
                                        setTabId(field.field_id);
                                      }}
                                    >
                                      Add Field
                                    </Button>
                                    <button
                                      onClick={() =>
                                        setExpandedTabIndex(isExpanded ? null : tabIndex)
                                      }
                                      className="text-gray-600 hover:text-gray-900"
                                    >
                                      {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                    </button>
                                  </div>
                                </div>

                                {/* Expandable Content */}
                                {isExpanded &&
                                  tabItem?.fields?.map((fielddata: any, index: number) => (
                                    <div
                                      key={fielddata.field_id || index}
                                      className="bg-white p-3 shadow-lg rounded"
                                    >
                                      <div className="border p-4 rounded">
                                        <div className="text-sm font-semibold mb-2">
                                          Field Name: {fielddata.label || 'Untitled Field'}
                                        </div>

                                        {fielddata.field_type === 'table' ? (
                                          <div className="flex justify-between w-full">
                                            <div className="flex justify-between flex-wrap gap-16 text-sm text-gray-700 w-[90%]">
                                              <span> Field type: {fielddata.field_type}</span>
                                              <span>Columns: {fielddata.columns?.length || 0}</span>
                                              <span>
                                                Rows:{' '}
                                                {fielddata.additional_config?.dimensions?.rows || 0}
                                              </span>
                                              <span>
                                                Row Header:{' '}
                                                {fielddata.additional_config?.headers?.rowHeader
                                                  ? 'Enabled'
                                                  : 'Not Applicable'}
                                              </span>
                                              <span>
                                                Expandable Table:{' '}
                                                {fielddata.additional_config?.additionalRows
                                                  ? 'Enabled'
                                                  : 'Disabled'}
                                              </span>
                                            </div>
                                            <div className="flex justify-center  gap-4">
                                              <button
                                                onClick={() => {
                                                  setIsDeleteTabsFieldPopupOpen(true);
                                                  setSelectedSection(fielddata);
                                                  setSectionId(section?.section_id);
                                                  setFieldTabsId(field?.field_id);
                                                  setTabId(tabItem.tab_id);
                                                }}
                                              >
                                                <Icons.trashIcon />
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex justify-between">
                                            <div className="text-sm text-gray-500">
                                              Field type: {fielddata.field_type}
                                            </div>
                                            <div className="flex justify-center  gap-4">
                                              <button
                                                onClick={() => {
                                                  setIsDeleteTabsFieldPopupOpen(true);
                                                  setSelectedSection(fielddata);
                                                  setSectionId(section?.section_id);
                                                  setFieldTabsId(field?.field_id);
                                                  setTabId(tabItem.tab_id);
                                                }}
                                              >
                                                <Icons.trashIcon />
                                              </button>
                                            </div>
                                          </div>
                                        )}

                                        <div className="flex items-center gap-2 bg-ds-mc-blue-100 p-2 rounded-md w-[96%] mt-2">
                                          <Icons.infoIcon />
                                          <p className="text-xs text-ds-orange-900">
                                            Field Name works as the title of the modal and does not
                                            appear for information, table and tabs.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ))}
              </div>
            ))
        )}

        {isPopUpOpen && selectedSection && (
          <Popup isOpen={isPopUpOpen} title="Delete Section" onClose={() => setIsPopUpOpen(false)}>
            <DeleteSection
              closePopUp={() => setIsPopUpOpen(false)}
              data={selectedSection}
              factSheetId={sectionData?.data?.fact_sheet_id || ''}
            />
          </Popup>
        )}

        {isPanelOpen && selectedSection && (
          <Panel
            isOpen={isPanelOpen}
            onClose={() => setIsPanelOpen(false)}
            title="Edit Section"
            size="medium"
          >
            <AddSection
              closePopUp={() => setIsPanelOpen(false)}
              data={selectedSection}
              factSheetId={sectionData?.data?.fact_sheet_id}
            />
          </Panel>
        )}

        {isDeleteFieldPopUpOpen && (
          <Popup
            isOpen={isDeleteFieldPopUpOpen}
            title="Delete Field"
            onClose={() => setIsDeleteFieldPopupOpen(false)}
          >
            <DeleteField
              closePopUp={() => setIsDeleteFieldPopupOpen(false)}
              FieldData={selectedSection}
              sectionId={sectionId}
              fieldId={selectedSection?.field_id || ''}
            />
          </Popup>
        )}

        {isOpen && (
          <Panel isOpen={isOpen} onClose={() => setIsOpen(false)} title="Add Section" size="medium">
            <AddSection closePopUp={() => setIsOpen(false)} />
          </Panel>
        )}
        {isFieldOpen && selectedSection && (
          <Panel
            isOpen={isFieldOpen}
            onClose={() => setIsFieldOpen(false)}
            title={!('field_id' in selectedSection) ? 'Add Field' : 'Edit Field'}
            size="veryLarge"
          >
            <AddField
              onClose={() => setIsFieldOpen(false)}
              data={selectedSection}
              sectionId={sectionId}
            />
          </Panel>
        )}
        {isTabsOpen && selectedSection && (
          <Panel
            isOpen={isTabsOpen}
            onClose={() => setIsTabsOpen(false)}
            title="Add Field"
            size="veryLarge"
          >
            <AddTab
              onClose={() => {
                setIsTabsOpen(false);
                // Keep the tab expanded after adding a field
              }}
              tabsData={selectedSection}
              tabsId={tabsId}
              sectionId={sectionId}
              onFieldAdded={() => handleTabAdded(expandedTabIndex as number)}
            />
          </Panel>
        )}
        {isDeleteTabsFieldPopUpOpen && (
          <Popup
            isOpen={isDeleteTabsFieldPopUpOpen}
            title="Delete Field"
            onClose={() => setIsDeleteTabsFieldPopupOpen(false)}
          >
            <DeleteTabsField
              closePopUp={() => setIsDeleteTabsFieldPopupOpen(false)}
              FieldData={selectedSection}
              sectionId={sectionId}
              FieldTabsId={FieldTabsId}
              tabId={tabsId}
            />
          </Popup>
        )}
        {/* Edit Option Modal (add or edit) */}
        {editOptionModalOpen && (
          <Panel
            isOpen={editOptionModalOpen}
            onClose={() => setEditOptionModalOpen(false)}
            title={selectedOption ? 'Edit Option' : 'Add Option'}
          >
            <EditOption
              mode={selectedOption ? 'edit' : 'add'}
              option={selectedOption}
              onSave={(updatedOption) => {
                setFieldData((prev: any[]) => {
                  if (selectedOption) {
                    // Edit existing option
                    return prev.map((section: any, sIdx: any) =>
                      sIdx === sectionId
                        ? {
                            ...section,
                            fields: section.fields.map((fld: any, fIdx: number) =>
                              fIdx === selectedFieldIdx
                                ? {
                                    ...fld,
                                    options: fld.options.map((opt: any, oIdx: number) =>
                                      oIdx === selectedOptionIdx ? updatedOption : opt
                                    ),
                                  }
                                : fld
                            ),
                          }
                        : section
                    );
                  } else {
                    // Add new option to the correct field
                    return prev.map((section: any, sIdx: any) =>
                      sIdx === sectionId
                        ? {
                            ...section,
                            fields: section.fields.map((fld: any, fIdx: number) =>
                              fIdx === selectedFieldIdx
                                ? {
                                    ...fld,
                                    options: [...fld.options, updatedOption],
                                  }
                                : fld
                            ),
                          }
                        : section
                    );
                  }
                });
                setEditOptionModalOpen(false);
              }}
              onCancel={() => setEditOptionModalOpen(false)}
            />
          </Panel>
        )}

        {/* Edit Field Modal (add or edit) */}
        {editFieldModalOpen && (
          <Panel
            isOpen={editFieldModalOpen}
            onClose={() => setEditFieldModalOpen(false)}
            title={selectedField ? 'Edit Field' : 'Add Field'}
          >
            <EditOptionField
              mode={selectedField ? 'edit' : 'add'}
              field={selectedField}
              onSave={(updatedField) => {
                setFieldData((prev: any[]) => {
                  if (selectedField) {
                    // Edit existing field
                    return prev.map((section: any, sIdx: any) =>
                      sIdx === sectionId
                        ? {
                            ...section,
                            fields: section.fields.map((fld: any, fIdx: number) =>
                              fIdx === selectedFieldIdx
                                ? {
                                    ...fld,
                                    options: fld.options.map((opt: any, oIdx: number) =>
                                      oIdx === selectedOptionIdx
                                        ? {
                                            ...opt,
                                            fields: opt.fields.map((f: any, fIdx: number) =>
                                              fIdx === selectedFieldIdx ? updatedField : f
                                            ),
                                          }
                                        : opt
                                    ),
                                  }
                                : fld
                            ),
                          }
                        : section
                    );
                  } else {
                    // Add new field to the correct option
                    return prev.map((section: any, sIdx: any) =>
                      sIdx === sectionId
                        ? {
                            ...section,
                            fields: section.fields.map((fld: any, fIdx: number) =>
                              fIdx === selectedFieldIdx
                                ? {
                                    ...fld,
                                    options: fld.options.map((opt: any, oIdx: number) =>
                                      oIdx === selectedOptionIdx
                                        ? {
                                            ...opt,
                                            fields: [...opt.fields, updatedField],
                                          }
                                        : opt
                                    ),
                                  }
                                : fld
                            ),
                          }
                        : section
                    );
                  }
                });
                setEditFieldModalOpen(false);
              }}
              onCancel={() => setEditFieldModalOpen(false)}
            />
          </Panel>
        )}
      </div>
    </div>
  );
};

export default Section;
